import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AdminHeaderActionsSlot } from '../contexts/AdminHeaderActionsContext';
import { InsurerHeaderActionsSlot } from '../contexts/InsurerHeaderActionsContext';
import { TabletopHeaderActionsSlot } from '../contexts/TabletopHeaderActionsContext';
import { BCPHeaderActionsSlot } from '../contexts/BCPHeaderActionsContext';
import { CrisisCommunicationsHeaderActionsSlot } from '../contexts/CrisisCommunicationsHeaderActionsContext';
import { useAuth } from '../contexts/AuthContext';
import {
  adminDashboardTabHref,
  getAdminDashboardTabLabel,
  isAdminDashboardArea,
  parseAdminDashboardTab,
} from '../utils/adminDashboardTabs';
import {
  getInsurerDashboardTabLabel,
  insurerDashboardTabHref,
  isInsurerAnalyticsPage,
  parseInsurerDashboardTab,
} from '../utils/insurerDashboardTabs';

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isOrgAdmin = user?.org_role === 'org_admin';
  const pathnames = location.pathname.split('/').filter((x) => x);
  const displayPathnames =
    location.pathname === '/admin/templates'
      ? pathnames.filter((segment) => segment !== 'templates')
      : pathnames;
  const onAdminDashboard = isAdminDashboardArea(location.pathname);
  const activeAdminTab = parseAdminDashboardTab(searchParams);
  const onInsurerDashboard = location.pathname === '/insurer-dashboard';
  const onInsurerAnalytics = isInsurerAnalyticsPage(location.pathname);
  const activeInsurerTab = parseInsurerDashboardTab(
    searchParams,
    isOrgAdmin,
    location.pathname
  );
  const onTabletopList =
    location.pathname === '/tabletop-exercises' ||
    location.pathname.replace(/\/$/, '') === '/tabletop-exercises';
  const onBCPList = location.pathname.replace(/\/$/, '') === '/bcp';
  const onCrisisCommunications =
    location.pathname.replace(/\/$/, '') === '/crisis-communications';
  const showHeaderActions = onTabletopList || onBCPList || onCrisisCommunications;

  const getBreadcrumbName = (path: string, index: number, fullPath: string) => {
    // Handle specific routes
    if (path === 'dashboard') return 'Dashboard';
    if (path === 'bcp') {
      if (pathnames[index + 1] === 'new') return 'New BCP';
      return 'BCP Plans';
    }
    if (path === 'new') {
      return pathnames[index - 1] === 'tabletop-exercises' ? 'New' : 'New BCP';
    }
    if (path === 'tabletop-exercises') return 'Tabletop Exercises';
    if (path === 'crisis-communications') return 'Crisis Communications';
    if (path === 'subscription-management') return 'Subscription Management';
    if (path === 'payment-history') return 'Payment History';
    if (path === 'billing-settings') return 'Billing Settings';
    if (path === 'profile') return 'Profile Settings';
    if (path === 'admin') return 'Admin';
    if (path === 'insurer-dashboard') return 'Insurer Dashboard';

    // Handle BCP ID routes
    if (!isNaN(Number(path)) && pathnames[index - 1] === 'bcp') {
      return `BCP #${path}`;
    }

    // Default: capitalize first letter
    return path.charAt(0).toUpperCase() + path.slice(1);
  };

  if (displayPathnames.length === 0) {
    return null;
  }

  if (onAdminDashboard) {
    const tabLabel = getAdminDashboardTabLabel(activeAdminTab);

    return (
      <nav className="bg-gray-50 border-b border-gray-200" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-2 min-w-0">
              <Link
                to="/"
                className="text-gray-400 hover:text-gray-500 transition-colors shrink-0"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="sr-only">Home</span>
              </Link>

              <svg className="h-4 w-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <Link
                to={adminDashboardTabHref('overview')}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors truncate"
              >
                Admin
              </Link>

              <svg className="h-4 w-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-sm font-medium text-gray-900 truncate">{tabLabel}</span>
            </div>

            <AdminHeaderActionsSlot />
          </div>
        </div>
      </nav>
    );
  }

  if (onInsurerDashboard || onInsurerAnalytics) {
    const tabLabel = getInsurerDashboardTabLabel(activeInsurerTab, isOrgAdmin);

    return (
      <nav className="bg-gray-50 border-b border-gray-200" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-2 min-w-0">
              <Link
                to="/insurer-dashboard"
                className="text-gray-400 hover:text-gray-500 transition-colors shrink-0"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="sr-only">Home</span>
              </Link>

              <svg className="h-4 w-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <Link
                to={insurerDashboardTabHref('clients')}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors truncate"
              >
                Insurer Dashboard
              </Link>

              <svg className="h-4 w-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-sm font-medium text-gray-900 truncate">{tabLabel}</span>
            </div>

            <InsurerHeaderActionsSlot />
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-gray-50 border-b border-gray-200" aria-label="Breadcrumb">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center py-3 ${showHeaderActions ? 'justify-between' : ''}`}>
          <div className="flex items-center space-x-2 min-w-0">
          <Link
            to="/"
            className="text-gray-400 hover:text-gray-500 transition-colors shrink-0"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="sr-only">Home</span>
          </Link>

          {displayPathnames.map((path, index) => {
            const isLast = index === displayPathnames.length - 1;
            const breadcrumbName = getBreadcrumbName(path, index, location.pathname);
            const breadcrumbPath = '/' + displayPathnames.slice(0, index + 1).join('/');

            return (
              <React.Fragment key={breadcrumbPath}>
                <svg className="h-4 w-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                {isLast ? (
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {breadcrumbName}
                  </span>
                ) : (
                  <Link
                    to={breadcrumbPath}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors truncate"
                  >
                    {breadcrumbName}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
          </div>
          {onTabletopList && <TabletopHeaderActionsSlot />}
          {onBCPList && <BCPHeaderActionsSlot />}
          {onCrisisCommunications && <CrisisCommunicationsHeaderActionsSlot />}
        </div>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
